import { useCallback, useState } from 'react';

/**
 * Manages open/closed state and dirty-section tracking for a PunchupAccordionGroup.
 * Encapsulates expand-all / collapse-all logic and cross-frame postMessages so
 * PunchupDashboard stays free of accordion internals.
 *
 * hasDirtySections: true when any section has unsaved changes — use as the `when`
 * prop for react-router <Prompt> to warn before SPA navigation.
 *
 * defaultOpenIds seeds the *initial* open set so those sections are already open at first paint.
 * Seed here rather than calling `setOpen` from an effect after data loads: that effect commits at or
 * after the host's reveal gate, so the user would watch the sections animate open and the iframe grow
 * once the dashboard is already visible — plus one extra transitionend-driven height post per section.
 *
 * Every returned callback is `useCallback`-stable. This is load-bearing, not cosmetic: they flow into
 * `AccordionContext` and thence into each `PunchupAccordionSection`'s `setDirty`. Anything that calls
 * `setDirty` inside an effect keyed on it (`useAccordionForm`, the sub-section group's dirty aggregation)
 * would otherwise re-run every render — and the sub-section group's setDirty(false)/setDirty(true) cleanup
 * pair turns that into an infinite render loop the moment a sub-panel reports dirty.
 */
export const usePunchupAccordion = (allSectionIds: readonly string[], defaultOpenIds: readonly string[] = []) => {
	// Lazy initialiser: `defaultOpenIds` is read once, on mount. Later changes to it are ignored by
	// design — after first paint the open set belongs to the user.
	const [openSections, setOpenSections] = useState<string[]>(() => [...defaultOpenIds]);
	const [dirtyIds, setDirtyIds] = useState<Set<string>>(new Set());

	const toggle = useCallback(
		(id: string) => setOpenSections((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id])),
		[]
	);

	const setOpen = useCallback(
		(id: string) => setOpenSections((prev) => (prev.includes(id) ? prev : [...prev, id])),
		[]
	);

	const expandAll = useCallback(() => {
		setOpenSections([...allSectionIds]);
		window.parent.postMessage({ action: 'accordion-expand-all' }, '*');
	}, [allSectionIds]);

	const collapseAll = useCallback(() => {
		setOpenSections([]);
		window.parent.postMessage({ action: 'accordion-collapse-all' }, '*');
	}, []);

	const setDirtySection = useCallback((id: string, isDirty: boolean) => {
		setDirtyIds((prev) => {
			if (prev.has(id) === isDirty) return prev; // no-op when unchanged — avoids a needless new Set + re-render
			const next = new Set(prev);
			if (isDirty) next.add(id);
			else next.delete(id);
			return next;
		});
	}, []);

	return {
		openSections,
		toggle,
		setOpen,
		expandAll,
		collapseAll,
		setDirtySection,
		allExpanded: openSections.length === allSectionIds.length,
		allCollapsed: openSections.length === 0,
		hasDirtySections: dirtyIds.size > 0,
	};
};
